import { configuracionFirebase } from '../configuracion/firebaseConfig';

// Reintento automático solo donde repetir es inocuo: lecturas y operaciones con
// clave de idempotencia que el servidor deduplica (misma clave → mismo pedido,
// mismo checkout, mismo evento). Nunca cobros manuales, reembolsos ni cambios de
// estado: esos los repite el admin tras consultar el estado.
const LECTURAS = new Set(['consultar_publico', 'detalle', 'pasarela_estado', 'credenciales_estado', 'mp_consultar_pago']);
const CON_CLAVE = new Set(['crear', 'mp_recuperar_checkout_publico', 'liberar_reservas']);
// Fallos de red, arranque en frío o resultado desconocido. No 'resource-exhausted'
// (límite de pedidos) ni errores de validación: repetirlos no cambia la respuesta.
const TRANSITORIOS = new Set(['unavailable', 'deadline-exceeded', 'internal']);
const INTENTOS = 3, BASE_MS = 400, TOPE_MS = 4000;

const codigoDe = error => String(error?.code || '').replace(/^functions\//, '');
export const reintentable = (datos, error) => TRANSITORIOS.has(codigoDe(error))
  && (LECTURAS.has(datos?.accion) || (CON_CLAVE.has(datos?.accion) && typeof datos.idempotencyKey === 'string' && datos.idempotencyKey.length > 0));
/** Backoff exponencial con jitter completo: espera al azar entre 0 y min(tope, base·2^intento). */
export const esperaReintento = (intento, azar = Math.random) => Math.round(azar() * Math.min(TOPE_MS, BASE_MS * 2 ** intento));

const dormir = ms => new Promise(resolver => setTimeout(resolver, ms));

// Por el mismo dominio de la app: Firebase Hosting reenvía /api/ordenOperacion a la
// Function (firebase.json). Así el navegador no hace la petición previa de CORS
// (OPTIONS), que contaba como otra ejecución: cada llamada costaba dos. «crear» y
// «buscar_publico» van directo porque su límite por red usa la IP que ve la Function.
// En desarrollo (localhost, sin Hosting) todo va directo.
const DIRECTAS = new Set(['crear', 'buscar_publico']);
export const rutaMismoDominio = (accion, ubicacion = typeof window === 'undefined' ? null : window.location) =>
  Boolean(ubicacion) && !DIRECTAS.has(accion) && ubicacion.protocol === 'https:'
  && !/^(localhost|127\.0\.0\.1|\[::1\])$/.test(ubicacion.hostname) ? `${ubicacion.origin}/api/ordenOperacion` : '';

// Solo la app y el SDK de Functions: la llamada no espera a descargar Firestore.
const cargarSdk = () => Promise.all([import('firebase/functions'), import('./firebaseApp')]);

// El comprador está llenando el pedido o la búsqueda (llamadas directas): se
// descarga el SDK y se abre la conexión con la Function (DNS + TLS, unos 0,3 s
// desde Perú) mientras escribe. No ejecuta nada en el servidor. Se renueva cada
// 8 s porque el navegador cierra las conexiones abiertas que no se usan.
const ORIGEN_FUNCIONES = `https://us-central1-${configuracionFirebase.projectId}.cloudfunctions.net`;
let ultimaPreparacion = 0;
export const prepararLlamadaDirecta = () => {
  const ahora = Date.now();
  if (typeof document === 'undefined' || ahora - ultimaPreparacion < 8000) return;
  if (!ultimaPreparacion) cargarSdk().catch(() => {});
  ultimaPreparacion = ahora;
  document.head.querySelector('link[data-preconexion-orden]')?.remove();
  const enlace = document.createElement('link');
  enlace.rel = 'preconnect';
  enlace.href = ORIGEN_FUNCIONES;
  enlace.crossOrigin = 'anonymous';
  enlace.setAttribute('data-preconexion-orden', '');
  document.head.appendChild(enlace);
};

export const operarOrden = async (datos, { esperar = dormir, azar = Math.random } = {}) => {
  try {
    const [{ getFunctions, httpsCallable, httpsCallableFromURL }, { default: app }] = await cargarSdk();
    const functions = getFunctions(app, 'us-central1'), ruta = rutaMismoDominio(datos?.accion);
    const llamar = ruta ? httpsCallableFromURL(functions, ruta) : httpsCallable(functions, 'ordenOperacion');
    for (let intento = 0; ; intento += 1) {
      try {
        return (await llamar(datos)).data;
      } catch (error) {
        if (intento + 1 >= INTENTOS || !reintentable(datos, error)) throw error;
        await esperar(esperaReintento(intento, azar));
      }
    }
  } catch (error) {
    throw Object.assign(new Error(error.message || 'No se pudo completar el pedido.'), { code: error.code, details: error.details });
  }
};
